<script setup lang="ts">
import { ref, watch } from 'vue';
import { useFieldTexts } from '@/composables';
import { VDateInput } from 'vuetify/labs/VDateInput';
import type { DateRangeType } from '@/types/statistics/DateRangeType';

type PropsType = {
    label?: string;
    disabled?: boolean;
    variant?: VDateInput['$props']['variant'];
    density?: VDateInput['$props']['density'];
    hideDetails?: boolean;
    readonly?: boolean;
    clearable?: boolean;
    prependIcon?: string;
    hideLabel?: boolean;
    isLabelTranslated?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    label: 'common.selectDateRange',
    disabled: false,
    variant: 'outlined',
    density: 'compact',
    hideDetails: true,
    clearable: true,
    readonly: false,
    hideLabel: false,
    prependIcon: 'mdi-calendar-range',
    isLabelTranslated: true,
});

const modelValue = defineModel<DateRangeType | null>('modelValue', { default: null });
const internalValue = ref<Date[] | null>(null);

const convertToArray = (range: DateRangeType): Date[] | null => {
    if (range) {
        return [range.start, range.end];
    }

    return null;
};

const convertToRange = (value: Date | Date[] | null | undefined): DateRangeType => {
    if (Array.isArray(value) && value.length >= 2) {
        return {
            start: value[0],
            end: value[value.length - 1],
        };
    } else if (value instanceof Date) {
        return {
            start: value,
            end: value,
        };
    }

    return null;
};

watch(
    modelValue,
    newValue => {
        internalValue.value = convertToArray(newValue || null);
    },
    { immediate: true },
);

const handleDateChange = (value: Date | Date[] | null | undefined) => {
    modelValue.value = convertToRange(value);
};

const { getLabel } = useFieldTexts({
    label: props.label,
    isTranslated: props.isLabelTranslated,
});

defineOptions({ inheritAttrs: false });
</script>

<template>
    <div class="b-date-range-picker">
        <div class="d-flex gap-2 align-center">
            <VDateInput
                v-model="internalValue"
                :label="!hideLabel ? getLabel : ''"
                :variant="variant"
                :density="density"
                :hide-details="hideDetails"
                :disabled="disabled"
                :readonly="readonly"
                :clearable="clearable"
                multiple="range"
                class="flex-grow-1"
                :prepend-icon="prependIcon"
                v-bind="$attrs"
                @update:model-value="handleDateChange"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.b-date-range-picker {
    min-width: 280px;
}
</style>
